import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import PaymentList from "./PaymentList";
import PaymentDetailModal from "./PaymentDetailModal";
import PaymentStatusModal from "./PaymentStatusModal";
import { ADMIN_ROLES } from "../../constants/types";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

const PaymentManagement = () => {
  const history = useHistory();
  const {
    admin,
    authState: { loading, error },
    getPaymentsListApiCall,
    getPaymentByIdApiCall,
    updatePaymentStatusApiCall,
    refundPaymentApiCall,
    exportPaymentsApiCall,
    clearErrors,
    showSnackbar,
  } = useAdminContext();

  // ============= RBAC HELPER =============
  const isSuperAdmin = () => admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  // State for payments and pagination
  const [payments, setPayments] = useState([]);
  const [paymentPagination, setPaymentPagination] = useState({
    total: 0,
    pageNo: 1,
    pageSize: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    payment_method: "all",
    transaction_type: "all",
    startDate: "",
    endDate: "",
    pageNo: 1,
    pageSize: 20,
  });

  // Modal states
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetailModalOpen, setPaymentDetailModalOpen] = useState(false);
  const [paymentStatusModalOpen, setPaymentStatusModalOpen] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    successfulPayments: 0,
    failedPayments: 0,
    pendingPayments: 0,
    refundedPayments: 0,
  });

  // Load payments on component mount and filter changes
  useEffect(() => {
    clearErrors();
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNo, filters.pageSize]);

  const loadPayments = async (customFilters = filters) => {
    try {
      const result = await getPaymentsListApiCall(customFilters);
      if (result) {
        setPayments(result.payments || []);
        setPaymentPagination(result.pagination || {});

        // Calculate statistics
        calculateStatistics(result.payments || []);
      }
    } catch (error) {
      console.error("Error loading payments:", error);
    }
  };

  const calculateStatistics = (paymentsList) => {
    const stats = {
      totalRevenue: 0,
      totalTransactions: paymentsList.length,
      successfulPayments: 0,
      failedPayments: 0,
      pendingPayments: 0,
      refundedPayments: 0,
    };

    paymentsList.forEach((payment) => {
      if (payment.status === "completed" || payment.status === "success") {
        stats.successfulPayments++;
        stats.totalRevenue += parseFloat(payment.amount || 0);
      } else if (payment.status === "failed") {
        stats.failedPayments++;
      } else if (payment.status === "pending") {
        stats.pendingPayments++;
      } else if (payment.status === "refunded") {
        stats.refundedPayments++;
      }
    });

    setStatistics(stats);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
      pageNo: filterName !== "pageNo" ? 1 : value,
    };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    loadPayments(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: "",
      status: "all",
      payment_method: "all",
      transaction_type: "all",
      startDate: "",
      endDate: "",
      pageNo: 1,
      pageSize: 20,
    };
    setFilters(clearedFilters);
    loadPayments(clearedFilters);
  };

  const handleRefresh = () => {
    loadPayments();
  };

  const handlePreviousPage = () => {
    if (paymentPagination.hasPrev) {
      handleFilterChange("pageNo", paymentPagination.pageNo - 1);
    }
  };

  const handleNextPage = () => {
    if (paymentPagination.hasNext) {
      handleFilterChange("pageNo", paymentPagination.pageNo + 1);
    }
  };

  const handleViewPayment = async (paymentId) => {
    try {
      const payment = await getPaymentByIdApiCall(paymentId);
      if (payment) {
        setSelectedPayment(payment);
        setPaymentDetailModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching payment details:", error);
    }
  };

  const handleViewUser = (userId) => {
    history.push(ADMIN_ROUTES.userDetail.replace(":id", userId));
  };

  // ============= RBAC PROTECTED HANDLERS =============

  const handleUpdatePaymentStatus = (payment) => {
    // RBAC: Only super admins can update payment status
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can update payment status", "error");
      return;
    }

    setSelectedPayment(payment);
    setPaymentStatusModalOpen(true);
  };

  const handleConfirmStatusUpdate = async (paymentId, newStatus, reason) => {
    // RBAC: Double-check super admin status
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can update payment status", "error");
      return;
    }

    try {
      const result = await updatePaymentStatusApiCall(paymentId, {
        status: newStatus,
        reason: reason,
      });

      if (result) {
        // Update the payment in the list
        setPayments((prevPayments) =>
          prevPayments.map((payment) =>
            payment.id === paymentId ? { ...payment, ...result } : payment
          )
        );
        setPaymentStatusModalOpen(false);
        setSelectedPayment(null);
        showSnackbar("Payment status updated successfully", "success");
        loadPayments(); // Refresh to update statistics
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
    }
  };

  const handleRefundPayment = async (paymentId, refundAmount, reason) => {
    // RBAC: Only super admins can process refunds
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can process refunds", "error");
      return;
    }

    try {
      const result = await refundPaymentApiCall(paymentId, {
        amount: refundAmount,
        reason: reason,
      });

      if (result) {
        // Update the payment in the list
        setPayments((prevPayments) =>
          prevPayments.map((payment) =>
            payment.id === paymentId ? { ...payment, ...result } : payment
          )
        );
        showSnackbar("Refund processed successfully", "success");
        loadPayments(); // Refresh to update statistics
      }
    } catch (error) {
      console.error("Error processing refund:", error);
    }
  };

  const handleExportPayments = async () => {
    // RBAC: Only super admins can export payments
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can export payment data", "error");
      return;
    }

    try {
      // Remove pagination params for export
      const { pageNo, pageSize, ...exportFilters } = filters;
      const result = await exportPaymentsApiCall(exportFilters);
      if (result) {
        // Create a download link for the CSV file
        const blob = new Blob([result], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `payments_export_${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSnackbar("Payments exported successfully", "success");
      }
    } catch (error) {
      console.error("Error exporting payments:", error);
    }
  };

  const handleCloseModals = () => {
    setPaymentDetailModalOpen(false);
    setPaymentStatusModalOpen(false);
    setSelectedPayment(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" color="text.primary" gutterBottom>
            Payment Management
          </Typography>
          <Typography variant="body1" color="text.primary">
            {isSuperAdmin()
              ? "Manage transactions, process refunds, and update payment statuses."
              : "View payment transactions and details (Read-only access)."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {isSuperAdmin() && (
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExportPayments}
              disabled={loading || payments.length === 0}
              color="success"
            >
              Export
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* RBAC Warning for Regular Admins */}
      {!isSuperAdmin() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access to payment management. Only super admins can
          update payment statuses, process refunds, or export payment data.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              {formatCurrency(statistics.totalRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {statistics.totalTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Transactions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="success.main" gutterBottom>
              {statistics.successfulPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Successful
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="error.main" gutterBottom>
              {statistics.failedPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Failed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="warning.main" gutterBottom>
              {statistics.pendingPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="info.main" gutterBottom>
              {statistics.refundedPayments}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Refunded
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by transaction ID, user..."
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="payment-method-label">Payment Method</InputLabel>
              <Select
                labelId="payment-method-label"
                label="Payment Method"
                value={filters.payment_method}
                onChange={(e) =>
                  handleFilterChange("payment_method", e.target.value)
                }
              >
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="paystack">Paystack</MenuItem>
                <MenuItem value="flutterwave">Flutterwave</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="transaction-type-label">
                Transaction Type
              </InputLabel>
              <Select
                labelId="transaction-type-label"
                label="Transaction Type"
                value={filters.transaction_type}
                onChange={(e) =>
                  handleFilterChange("transaction_type", e.target.value)
                }
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="coin_purchase">Coin Purchase</MenuItem>
                <MenuItem value="subscription">Subscription</MenuItem>
                <MenuItem value="gift">Gift</MenuItem>
                <MenuItem value="refund">Refund</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                fullWidth
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Date Range Filters */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              size="small"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              size="small"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Payment List */}
      <PaymentList
        payments={payments}
        loading={loading}
        pagination={paymentPagination}
        onViewPayment={handleViewPayment}
        onViewUser={handleViewUser}
        onUpdateStatus={handleUpdatePaymentStatus}
        onRefund={handleRefundPayment}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* Payment Detail Modal */}
      <PaymentDetailModal
        open={paymentDetailModalOpen}
        payment={selectedPayment}
        onClose={handleCloseModals}
        onUpdateStatus={handleUpdatePaymentStatus}
        onRefund={handleRefundPayment}
        loading={loading}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* Payment Status Update Modal - Only for Super Admins */}
      {isSuperAdmin() && (
        <PaymentStatusModal
          open={paymentStatusModalOpen}
          payment={selectedPayment}
          onClose={handleCloseModals}
          onConfirm={handleConfirmStatusUpdate}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default PaymentManagement;
